import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import './styles/index.css'
import { AuthProvider } from './context/AuthContext'
import { clearBrowserStorage } from './lib/storage'
import * as Sentry from "@sentry/react";

type SentryFallbackProps = {
  error: unknown
  componentStack: string
  resetError: () => void
}

function SentryFallback({ error, componentStack, resetError }: SentryFallbackProps) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600 text-xl mb-4">
          !
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">Something went wrong</h2>
        <p className="text-slate-600 mt-2">
          We hit an unexpected issue while loading this page.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => {
              clearBrowserStorage()
              resetError()
              window.location.href = '/'
            }}
            className="px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            Go to home
          </button>
        </div>
        <details className="mt-5 text-left">
          <summary className="cursor-pointer text-sm text-slate-500">Technical details</summary>
          <pre className="mt-2 max-h-40 overflow-auto text-xs bg-slate-100 text-slate-700 p-3 rounded">
            {String(error)}
          </pre>
          <pre className="mt-2 max-h-40 overflow-auto text-xs bg-slate-100 text-slate-700 p-3 rounded">
            {componentStack}
          </pre>
        </details>
      </div>
    </div>
  )
}

function CrashForBoundaryTest() {
  throw new Error('Sentry fallback test')
}

Sentry.init({
  dsn: "https://622e2db5b0767285db7c615cd96ce4dd@o4510888924676096.ingest.us.sentry.io/4510888930050048",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    // send console.log, console.warn, and console.error calls as logs to Sentry
    Sentry.consoleLoggingIntegration({ levels: ["debug", "info", "warn", "error", "log"] }),
  ],
  // Enable logs to be sent to Sentry
  enableLogs: true,
});

const rootEl = createRoot(document.getElementById('root')!, {
  // Error reporting: captures all errors
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  onRecoverableError: Sentry.reactErrorHandler(),
})


rootEl.render(
  <BrowserRouter>
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <AuthProvider>
        <App />
      </AuthProvider>
    </Sentry.ErrorBoundary>
  </BrowserRouter>
)
